/**
 * Barrel export + dispatcher for the LEGACY per-kind bag adapters.
 *
 * Adapters convert `EntityPreview` → `RenderableEntity` by parsing the
 * `properties` string bag. The dispatcher (`entityToRenderable`) picks the
 * adapter by entity.type and delegates.
 *
 * DEFUSE S11 — this pipeline is NO LONGER the primary render path. The live
 * lift goes typed-first (`./typedFirstRenderable.ts` →
 * `./typed/entityToRenderableTyped`, reading the canonical `DrawingEntity`
 * without the bag round-trip). This dispatcher remains REACHABLE only from:
 *
 *   1. the bag-fallback kinds (`BAG_FALLBACK_RENDER_KINDS`: Viewport /
 *      Rectangular Associative Array — each leaves when its typed adapter
 *      slice lands). Dimension (S8) and Multileader (S9) LANDED typed
 *      adapters and left this set — their `case` arms below stay reachable
 *      only via routes 2/3 (Multileader's `case` still returns `null` here;
 *      its typed cutover routes through `typedFirstRenderable.ts`'s
 *      explicit-dispatch branch instead of this legacy dispatcher);
 *   2. the draft-preview channel (`draft/draftPreviewLayer.tsx`) — an
 *      in-flight draft is a command-engine bag preview with NO typed
 *      document entity until commit (typed drafts are S15/S16 scope);
 *   3. typed-resolution misses (defensive — a preview id absent from the
 *      document).
 *
 * The per-kind adapter modules below therefore survive the S11 cutover
 *  — deleting one requires BOTH its fallback kind to be typed AND the draft
 * channel to stop producing that kind's bag preview.
 */

import type { EntityPreview } from "@dxf-ai-editor/contracts";

import type { RenderableEntity } from "../types";

import { lineToRenderable } from "./lineToRenderable";
import { pointToRenderable } from "./pointToRenderable";
import { circleToRenderable } from "./circleToRenderable";
import { arcToRenderable } from "./arcToRenderable";
import { ellipseToRenderable } from "./ellipseToRenderable";
import { splineToRenderable } from "./splineToRenderable";
import { lwpolylineToRenderable } from "./lwpolylineToRenderable";
import { polylineToRenderable } from "./polylineToRenderable";
import { hatchToRenderable } from "./hatchToRenderable";
import { wipeoutToRenderable } from "./wipeoutToRenderable";
import { viewportToRenderable } from "./viewportToRenderable";
import { revisionCloudToRenderable } from "./revisionCloudToRenderable";
import { arrayRectToInstanced } from "./arrayRectToInstanced";
import { textToRenderable } from "./textToRenderable";
import { mtextToRenderable } from "./mtextToRenderable";
import { dimensionToPrimitives } from "../dimensions/dimensionToPrimitives";

import type { AdapterContext } from "./adapterContext";

export {
  lineToRenderable,
  pointToRenderable,
  circleToRenderable,
  arcToRenderable,
  ellipseToRenderable,
  splineToRenderable,
  lwpolylineToRenderable,
  polylineToRenderable,
  hatchToRenderable,
  wipeoutToRenderable,
  viewportToRenderable,
  revisionCloudToRenderable,
  arrayRectToInstanced,
  textToRenderable,
  mtextToRenderable,
};

export type { AdapterContext } from "./adapterContext";
export {
  buildRenderableShell,
  boundsFromPoints,
  parseLinetypeScale,
  resolveShellColorHex,
} from "./adapterContext";

export {
  buildMarkerPrototype,
  getMarkerPrototype,
  effectiveMarkerRadius,
  POINT_MARKER_PROTOTYPES,
  type MarkerPrototype,
  type MarkerSegment,
  type MarkerCircle,
} from "./pointMarkerMeshes";

export {
  tessellationSegmentCount,
  tessellateArc,
  tessellateEllipse,
  bulgeToArc,
  tessellateBulgeSegment,
  type Polyline,
  type BulgeArc,
} from "./arcEllipseSpline";

/**
 * Top-level dispatcher. Returns `null` for entity kinds PR-B does not
 * yet handle — caller skips and the entity stays out of the spatial
 * index until the relevant follow-up PR lands.
 */
export function entityToRenderable(
  entity: EntityPreview,
  ctx: AdapterContext,
): RenderableEntity | null {
  switch (entity.type) {
    case "Line":
      return lineToRenderable(entity, ctx);
    case "Point":
      return pointToRenderable(entity, ctx);
    case "Circle":
      return circleToRenderable(entity, ctx);
    case "Arc":
      return arcToRenderable(entity, ctx);
    case "Ellipse":
      return ellipseToRenderable(entity, ctx);
    case "Spline":
      return splineToRenderable(entity, ctx);
    case "LWPOLYLINE":
      return lwpolylineToRenderable(entity, ctx);
    // PR-D handles: Hatch, Wipeout, Viewport, Revision Cloud,
    //               Rectangular Associative Array
    case "Hatch":
      return hatchToRenderable(entity, ctx);
    case "Wipeout":
      return wipeoutToRenderable(entity, ctx);
    case "Viewport":
      return viewportToRenderable(entity, ctx);
    case "Revision Cloud":
      return revisionCloudToRenderable(entity, ctx);
    case "Rectangular Associative Array":
      return arrayRectToInstanced(entity, ctx);
    // PR-E2 — Text + MText (single-paragraph MText; full DXF code
    // parser lands in PR-E3).
    case "Text":
      return textToRenderable(entity, ctx);
    case "MText":
      return mtextToRenderable(entity, ctx);
    // PR-F handles: Dimension, Multileader
    // PR-G handles: Block (full nested INSERT walk) — see the `case "Block"`
    // note below.
    case "Dimension":
      // PR-F P1 — Dimension dispatcher hand-off. The adapter
      // (`dimensionToPrimitives`) emits a RenderableEntity with
      // `geometry: { kind: "dimension", primitives, styleName }`.
      // Downstream consumers (BatchedLayer flatten, mesh factories,
      // text synthesizer) read `geometry.primitives` to materialise
      // arrows + text alongside the line-class primitives.
      return dimensionToPrimitives(entity, ctx);
    case "Block":
      // A typed INSERT never reaches this bag switch on a RENDER path: both
      // render branches dispatch it BEFORE `renderableFromDrawingEntity`
      // (legacy `SceneBulkLoad` → flat children; cache-direct / zoom re-lift
      // `liftRenderableForId` → `block-insert` wrapper + satellites), all via
      // `scene/liftBlockInsert.ts`. This arm is reached only by (1) the
      // pick-candidate lift (`StagePointerHandlers`), whose `null` falls back
      // to the spatial stub — bbox pick, same as `distance.ts`'s
      // `block-insert` arm; (2) typed-resolution misses (no typed INSERT ⇒
      // nothing to flatten — the bag flatten was deleted in DEFUSE S11);
      // (3) the draft channel, which emits no Block previews.
      return null;
    case "Multileader":
      return null;
    default:
      return null;
  }
}
